// app/javascript/components/shared/ThemeProvider.tsx
import React, { createContext, useContext, useEffect, useState, useMemo } from 'react'
import {
  ThemeDefinition,
  BUILTIN_THEMES,
  BUILTIN_THEME_MAP,
  DbTheme,
  mergeThemes,
  applyTheme,
  resolveInitialThemeName,
} from '@/lib/themes'

interface ThemeContextValue {
  themeName: string
  currentTheme: ThemeDefinition
  mode: 'light' | 'dark'
  availableThemes: ThemeDefinition[]
  setTheme: (themeName: string) => void
  toggleMode: () => void
}

const ThemeContext = createContext<ThemeContextValue>({
  themeName: 'glass-light',
  currentTheme: BUILTIN_THEMES[0],
  mode: 'light',
  availableThemes: BUILTIN_THEMES,
  setTheme: () => {},
  toggleMode: () => {},
})

interface ThemeProviderProps {
  children: React.ReactNode
  initialThemeName?: string | null
  dbThemes?: DbTheme[]
  portal?: 'student' | 'admin'
}

export function ThemeProvider({
  children,
  initialThemeName,
  dbThemes,
  portal = 'student',
}: ThemeProviderProps) {
  const availableThemes = useMemo(() => {
    if (dbThemes && dbThemes.length > 0) {
      return mergeThemes(dbThemes)
    }
    return BUILTIN_THEMES
  }, [dbThemes])

  const [themeName, setThemeNameState] = useState<string>(() => {
    return resolveInitialThemeName(initialThemeName, 'glass-light')
  })

  const currentTheme = useMemo(() => {
    return availableThemes.find((t) => t.name === themeName) || BUILTIN_THEME_MAP[themeName] || BUILTIN_THEMES[0]
  }, [themeName, availableThemes])

  useEffect(() => {
    applyTheme(currentTheme)
    document.body.setAttribute('data-portal', portal)
    if (typeof window !== 'undefined') {
      localStorage.setItem('themeName', themeName)
    }
  }, [currentTheme, themeName, portal])

  const setTheme = (newThemeName: string) => {
    setThemeNameState(newThemeName)

    const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content

    fetch('/settings/theme', {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken || '',
        'Accept': 'application/json',
      },
      body: JSON.stringify({ theme_name: newThemeName }),
    }).catch(() => {})
  }

  const toggleMode = () => {
    const targetMode = currentTheme.mode === 'light' ? 'dark' : 'light'
    // Find the paired theme in the same theme family (e.g. solarized-light <-> solarized-dark)
    const pairedTheme = availableThemes.find(
      (t) => t.family === currentTheme.family && t.mode === targetMode
    )

    if (pairedTheme) {
      setTheme(pairedTheme.name)
    } else {
      // Fallback if family pair not found
      const fallback = availableThemes.find((t) => t.mode === targetMode)
      if (fallback) setTheme(fallback.name)
    }
  }

  return (
    <ThemeContext.Provider
      value={{
        themeName,
        currentTheme,
        mode: currentTheme.mode,
        availableThemes,
        setTheme,
        toggleMode,
      }}
    >
      {children}
    </ThemeContext.Provider>
  )
}

export const useTheme = () => useContext(ThemeContext)
